import ReactDOM from "react-dom";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { switchEndpoint } from "../../../../../redux/actions/endpoints";
import capitalize from "../../../../../capitalize";
import moment from "moment";
import { storage } from "../../../../../scopedStorage";

const STATUS_CONFIG = {
  operational: {
    label: "Operational",
    badge: "badge-operational",
    icon: "✓",
  },
  under_maintenance: {
    label: "Under Maintenance",
    badge: "badge-maintenance",
    icon: "—",
  },
  decommissioned: {
    label: "Decommissioned",
    badge: "badge-decommissioned",
    icon: "⚙",
  },
  faulty: {
    label: "Faulty",
    badge: "badge-faulty",
    icon: "⚙",
  }
};

const removeSpecialChar = (str) =>str.replace(/[^a-zA-Z0-9 ]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

// --- StatusBadge ---
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status];
  return (
    <span className={`badge ${config.badge}`}>
      <span className="badge-dot" />
      {config.label}
    </span>
  );
};

// --- ActionDropdown ---

const ActionDropdown = ({ equipment, onStatusChange, isUpdating }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX,
      });
    }
    setOpen((prev) => !prev);
  };

const handleSelect = (newStatus) => {
  setOpen(false);
  onStatusChange(equipment, newStatus); // ← pass full equipment, not just equipment.id
};

  const otherStatuses = Object.keys(STATUS_CONFIG).filter(
    (s) => s !== equipment.status
  );

  const menu = open
    ? ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: menuPos.top,
            left: menuPos.left,
            transform: "translateX(-100%)",
            minWidth: 160,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            overflow: "hidden",
            zIndex: 9999,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
          role="menu"
        >
          {otherStatuses.map((status) => (
            <button
              key={status}
              className="dropdown-item"
              role="menuitem"
              onClick={() => handleSelect(status)}
            >
              <span className={`dropdown-dot dot-${status}`} />
              Set as {STATUS_CONFIG[status].label}
            </button>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <div className="dropdown-wrapper">
      <button
        ref={btnRef}
        className="action-btn"
        onClick={handleToggle}
        disabled={isUpdating}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {isUpdating ? (
          <span className="spinner" />
        ) : (
          <>
            Update <span className="chevron">{open ? "▲" : "▼"}</span>
          </>
        )}
      </button>
      {menu}
    </div>
  );
};

// --- EquipmentTable ---
const EquipmentTable = ({ isLoading, setIsloading }) => {
  const [equipments, setEquipments] = useState([]);
  const [updatingIds, setUpdatingIds] = useState(new Set());

  useEffect(() => {
    fetchEquipments();
  }, []);

  const fetchEquipments = () => {
    const token = storage.getItem("token");
    setIsloading(true);
    axios.get(`${switchEndpoint}/collection-center/equipments`,  {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setEquipments(response?.data);
        setIsloading(false);
      }).catch (error => {
        toast.error(error?.response?.data?.message || "Failed to fetch equipments", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            pauseOnHover: true,
            theme: "light",
        });
      }) 
            
  };

  // const handleStatusChange = async (equipment, newStatus) => {
  //   const token = storage.getItem("token");
  //   setUpdatingIds((prev) => new Set(prev).add(equipment.id));
  //   try {
  //       await axios.patch(
  //       `${switchEndpoint}/collection-center/update-equipment-status/${equipment.id}`, 
  //       { status: newStatus },  {
  //       headers: { Authorization: `Bearer ${token}` },
  //     });

  //       setEquipments((prev) =>
  //       prev.map((eq) =>
  //           eq.id === equipment.id ? { ...eq, status: newStatus } : eq
  //       )
  //       );

  //           toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`, {
  //           position: "top-right",
  //           autoClose: 3000,
  //           hideProgressBar: false,
  //           pauseOnHover: true,
  //           theme: "light",
  //           });
  //       } catch (error) {
  //           toast.error(error?.response?.data?.message || "Failed to update status", {
  //           position: "top-right",
  //           autoClose: 3000,
  //           hideProgressBar: false,
  //           pauseOnHover: true,
  //           theme: "light",
  //           });
  //       } finally {
  //           setUpdatingIds((prev) => {
  //           const updated = new Set(prev);
  //           updated.delete(equipment.id);
  //           return updated;
  //           });
  //       }
  //   }; 

  const handleStatusChange = async (equipment, newStatus) => {
  const token = storage.getItem("token");
  setUpdatingIds((prev) => new Set(prev).add(equipment.id));
  try {
    const response = await axios.patch(
      `${switchEndpoint}/collection-center/update-equipment-status/${equipment.id}`,
      { status: newStatus },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    console.log(response.data); // ← check the shape first

    setEquipments((prev) =>
      prev.map((eq) =>
        eq.id === equipment.id ? { ...eq, ...response.data } : eq
      )
    );

    toast.success(`Status updated to ${STATUS_CONFIG[newStatus].label}`, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      pauseOnHover: true,
      theme: "light",
    });
  } catch (error) {
    toast.error(error?.response?.data?.message || "Failed to update status", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      pauseOnHover: true,
      theme: "light",
    });
  } finally {
    setUpdatingIds((prev) => {
      const updated = new Set(prev);
      updated.delete(equipment.id);
      return updated;
    });
  }
};

  if (isLoading) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", padding: "2rem", color: "#6b7280", fontSize: 14 }}>
  Loading equipment...
</div>;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="table-wrapper">
        <table className="eq-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Model</th>
              <th>Serial Number</th>
              <th>Status</th>
              <th>Date Updated</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {equipments.map((eq) => (
              <tr key={eq.id}>
                <td className="eq-id">{eq.id}</td>
                <td className="eq-name">{eq.name}</td>
                <td className="eq-muted">{eq.model}</td>
                <td className="eq-muted">{eq.serial_number}</td>
                <td className="eq-muted">
                  {/* <StatusBadge status={eq.status} /> */}
                  {removeSpecialChar(eq.status)}
                </td>
                <td className="eq-muted">{moment(eq.updated_at).format("DD/MM/YYYY")}</td>
                <td style={{ textAlign: "right" }}>
                  <ActionDropdown
                    equipment={eq}
                    onStatusChange={handleStatusChange}
                    isUpdating={updatingIds.has(eq.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

const styles = `
  .table-wrapper {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow: visible;
    background: #fff;
    width: 100% !important;
    display: block;
  }
  .eq-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    overflow: visible;
  }

  .eq-table thead tr:first-child th:first-child { border-top-left-radius: 10px; }
  .eq-table thead tr:first-child th:last-child  { border-top-right-radius: 10px; }
  .eq-table tbody tr:last-child td:first-child  { border-bottom-left-radius: 10px; }
  .eq-table tbody tr:last-child td:last-child   { border-bottom-right-radius: 10px; }
  .eq-table th {
    text-align: left;
    padding: 10px 16px;
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
  }
  .eq-table td {
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    color: #111827;
  }
  .eq-table tr:last-child td { border-bottom: none; }
  .eq-table tr:hover td { background: #f9fafb; }
  .eq-id { font-family: monospace; font-size: 12px; color: #6b7280; }
  .eq-name { font-weight: 500; }
  .eq-muted { color: #6b7280; }
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    padding: 3px 10px;
    border-radius: 999px;
  }
  .badge-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  .badge-active      { background: #dcfce7; color: #15803d; }
  .badge-inactive    { background: #f3f4f6; color: #6b7280; }
  .badge-maintenance { background: #fef9c3; color: #a16207; }
  .badge-fault       { background: #fee2e2; color: #dc2626; }
  .dropdown-wrapper { position: relative; display: inline-block; }
  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    padding: 6px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    color: #374151;
    cursor: pointer;
    min-width: 96px;
    justify-content: center;
  }
  .action-btn:hover:not(:disabled) { background: #f9fafb; }
  .action-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .chevron { font-size: 10px; }
  .dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    min-width: 160px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    z-index: 50;
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  }
  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 9px 14px;
    font-size: 13px;
    border: none;
    background: transparent;
    color: #374151;
    cursor: pointer;
    text-align: left;
  }
  .dropdown-item:hover { background: #f9fafb; }
  .dropdown-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .dot-active      { background: #16a34a; }
  .dot-inactive    { background: #9ca3af; }
  .dot-maintenance { background: #ca8a04; }
  .dot-fault       { background: #dc2626; }
  .spinner {
    display: inline-block;
    width: 14px; height: 14px;
    border: 2px solid #d1d5db;
    border-top-color: #6b7280;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .loading {
    padding: 2rem;
    text-align: center;
    color: #6b7280;
    font-size: 14px;
  }
`;

export default EquipmentTable;